import _isNil from 'lodash/isNil';
import {
  Access,
  AccessComponentEnum,
  AccessCuInputObject,
  AccessLevelEnum,
  AccessPolicyEnum,
  AccessScopeLevelEnum,
} from 'graphql-common';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import { PlanState } from '@lib/enums/plan';
import { SelectOption } from '@lib/components/Select/types';

export function componentHasScopeLevel(component: AccessComponentEnum) {
  return (
    component === AccessComponentEnum.InterventionsManagementCategoriesScope ||
    component === AccessComponentEnum.AssetsManagementCategoriesScope ||
    component === AccessComponentEnum.DocumentationManagementCategoriesScope
  );
}

export enum AccessesFieldType {
  FullOptions = 'fullOptions',
  ShortOptions = 'shortOptions',
  InterventionCategoriesSelect = 'interventionCategoriesSelect',
  AssetCategoriesSelect = 'assetCategoriesSelect',
  DocumentationCategoriesSelect = 'documentationCategoriesSelect',
}

export type AccessFieldValue = ReturnType<typeof getEmptyAccessValue>;
export type AccessesFieldValues = { [key: string]: AccessFieldValue };

export function getAccessesOrder() {
  return [
    AccessComponentEnum.SitesManagement,
    AccessComponentEnum.UsersManagement,
    AccessComponentEnum.DepartmentsManagement,
    AccessComponentEnum.TasksManagement,
    AccessComponentEnum.TasksPerforming,
    AccessComponentEnum.FormCategoriesManagement,
    AccessComponentEnum.InterventionsManagement,
    AccessComponentEnum.InterventionsManagementCategoriesScope,
    AccessComponentEnum.InterventionCategoriesManagement,
    AccessComponentEnum.AssetsManagement,
    AccessComponentEnum.AssetsManagementCategoriesScope,
    AccessComponentEnum.AssetsCounterReading,
    AccessComponentEnum.AssetCategoriesManagement,
    AccessComponentEnum.HomePageManagement,
    AccessComponentEnum.DocumentationManagement,
    AccessComponentEnum.DocumentationManagementCategoriesScope,
    AccessComponentEnum.DocumentationAnalytics,
    AccessComponentEnum.DocumentationCategoriesManagement,
    AccessComponentEnum.AiAssistance,
  ];
}

type AccessField = {
  value: AccessComponentEnum;
  labelTransKey: string;
  descriptionTransKey?: string;
  fieldType: AccessesFieldType;
  subOptions?: AccessField[];
};

export const InterventionsManagementAccessConfig = {
  value: AccessComponentEnum.InterventionsManagement,
  labelTransKey: 'intervention-management',
  descriptionTransKey: 'intervention-management-description',
  fieldType: AccessesFieldType.FullOptions,
};

export const DocumentationManagementAccessConfig = {
  value: AccessComponentEnum.DocumentationManagement,
  labelTransKey: 'documentation-management',
  descriptionTransKey: 'documentation-management-description',
  fieldType: AccessesFieldType.FullOptions,
};

export const AiAssistanceAccessConfig = {
  value: AccessComponentEnum.AiAssistance,
  labelTransKey: 'ai-assistance',
  descriptionTransKey: 'ai-assistance-description',
  fieldType: AccessesFieldType.ShortOptions,
};

export function getAccessesMap(): { [key: string]: AccessField } {
  return {
    [AccessComponentEnum.SitesManagement]: {
      value: AccessComponentEnum.SitesManagement,
      labelTransKey: 'site-sector-management',
      descriptionTransKey: 'site-sector-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.UsersManagement]: {
      value: AccessComponentEnum.UsersManagement,
      labelTransKey: 'employee-management',
      descriptionTransKey: 'employee-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.DepartmentsManagement]: {
      value: AccessComponentEnum.DepartmentsManagement,
      labelTransKey: 'departments',
      descriptionTransKey: 'departments-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.TasksManagement]: {
      value: AccessComponentEnum.TasksManagement,
      labelTransKey: 'task-management',
      descriptionTransKey: 'task-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.TasksPerforming]: {
      value: AccessComponentEnum.TasksPerforming,
      labelTransKey: 'performing-tasks',
      descriptionTransKey: 'performing-tasks-description',
      fieldType: AccessesFieldType.ShortOptions,
    },
    [AccessComponentEnum.FormCategoriesManagement]: {
      value: AccessComponentEnum.FormCategoriesManagement,
      labelTransKey: 'forms-categories-management',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.InterventionsManagement]:
      InterventionsManagementAccessConfig,
    [AccessComponentEnum.InterventionsManagementCategoriesScope]: {
      value: AccessComponentEnum.InterventionsManagementCategoriesScope,
      labelTransKey: 'intervention-categories-scope-management',
      descriptionTransKey:
        'intervention-categories-scope-management-description',
      fieldType: AccessesFieldType.InterventionCategoriesSelect,
    },
    [AccessComponentEnum.InterventionCategoriesManagement]: {
      value: AccessComponentEnum.InterventionCategoriesManagement,
      labelTransKey: 'intervention-categories-management',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.AssetsManagement]: {
      value: AccessComponentEnum.AssetsManagement,
      labelTransKey: 'equipment-management',
      descriptionTransKey: 'equipment-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.AssetsManagementCategoriesScope]: {
      value: AccessComponentEnum.AssetsManagementCategoriesScope,
      labelTransKey: 'equipment-categories-scope-management',
      descriptionTransKey: 'equipment-categories-scope-management-description',
      fieldType: AccessesFieldType.AssetCategoriesSelect,
    },
    [AccessComponentEnum.AssetsCounterReading]: {
      value: AccessComponentEnum.AssetsCounterReading,
      labelTransKey: 'counter-readings',
      descriptionTransKey: 'counter-readings-description',
      fieldType: AccessesFieldType.ShortOptions,
    },
    [AccessComponentEnum.AssetCategoriesManagement]: {
      value: AccessComponentEnum.AssetCategoriesManagement,
      labelTransKey: 'equipment-categories-management',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.HomePageManagement]: {
      value: AccessComponentEnum.HomePageManagement,
      labelTransKey: 'home-page-management',
      descriptionTransKey: 'home-page-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.DocumentationManagement]: {
      value: AccessComponentEnum.DocumentationManagement,
      labelTransKey: 'documentation-management',
      descriptionTransKey: 'documentation-management-description',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.DocumentationManagementCategoriesScope]: {
      value: AccessComponentEnum.DocumentationManagementCategoriesScope,
      labelTransKey: 'documentation-categories-scope-management',
      descriptionTransKey:
        'documentation-categories-scope-management-description',
      fieldType: AccessesFieldType.DocumentationCategoriesSelect,
    },
    [AccessComponentEnum.DocumentationAnalytics]: {
      value: AccessComponentEnum.DocumentationAnalytics,
      labelTransKey: 'documentation-analytics',
      fieldType: AccessesFieldType.ShortOptions,
    },
    [AccessComponentEnum.DocumentationCategoriesManagement]: {
      value: AccessComponentEnum.DocumentationCategoriesManagement,
      labelTransKey: 'documentation-categories-management',
      fieldType: AccessesFieldType.FullOptions,
    },
    [AccessComponentEnum.AiAssistance]: AiAssistanceAccessConfig,
  };
}

export function getAccessesManagementOptions() {
  const accessMap = getAccessesMap();
  return [
    { ...accessMap[AccessComponentEnum.SitesManagement] },
    {
      ...accessMap[AccessComponentEnum.UsersManagement],
      subOptions: [{ ...accessMap[AccessComponentEnum.DepartmentsManagement] }],
    },
    {
      ...accessMap[AccessComponentEnum.TasksManagement],
      subOptions: [
        { ...accessMap[AccessComponentEnum.TasksPerforming] },
        { ...accessMap[AccessComponentEnum.FormCategoriesManagement] },
      ],
    },
    {
      ...accessMap[AccessComponentEnum.InterventionsManagement],
      subOptions: [
        {
          ...accessMap[
            AccessComponentEnum.InterventionsManagementCategoriesScope
          ],
        },
        { ...accessMap[AccessComponentEnum.InterventionCategoriesManagement] },
      ],
    },
    {
      ...accessMap[AccessComponentEnum.AssetsManagement],
      subOptions: [
        { ...accessMap[AccessComponentEnum.AssetsManagementCategoriesScope] },
        { ...accessMap[AccessComponentEnum.AssetsCounterReading] },
        { ...accessMap[AccessComponentEnum.AssetCategoriesManagement] },
      ],
    },
    { ...accessMap[AccessComponentEnum.HomePageManagement] },
    {
      ...accessMap[AccessComponentEnum.DocumentationManagement],
      subOptions: [
        {
          ...accessMap[
            AccessComponentEnum.DocumentationManagementCategoriesScope
          ],
        },
        { ...accessMap[AccessComponentEnum.DocumentationAnalytics] },
        { ...accessMap[AccessComponentEnum.DocumentationCategoriesManagement] },
      ],
    },
    { ...accessMap[AccessComponentEnum.AiAssistance] },
  ];
}

export function getAccessLevelOptions() {
  return [
    {
      value: AccessLevelEnum.Write,
      labelTransKey: 'access-level-editor',
    },
    {
      value: AccessLevelEnum.Read,
      labelTransKey: 'access-level-viewer',
    },
    {
      value: AccessLevelEnum.LimitedRead,
      labelTransKey: 'access-level-restricted',
      data: {
        isNegative: true,
      },
    },
  ];
}

export function getAccessLevelShortOptions() {
  return [
    {
      value: AccessLevelEnum.Write,
      labelTransKey: 'allowed',
    },
    {
      value: AccessLevelEnum.LimitedRead,
      labelTransKey: 'access-level-restricted',
      data: {
        isNegative: true,
      },
    },
  ];
}

export function getAccessLevelOption(level: AccessLevelEnum) {
  return getAccessLevelOptions().find(({ value }) => value === level);
}

export function getAccessCategoriesScopeIds(
  data: Access[] | null,
  findByComponent: AccessComponentEnum,
) {
  if (!data || !data?.length || !findByComponent) return [];
  const catData = data.find(({ component }) => component === findByComponent);
  return catData?.itemIds || [];
}

export function getAccessesPreparedDataFromValues(
  accessesValues: AccessesFieldValues,
  prevAccessesValues: AccessesFieldValues,
  useDestroy?: boolean,
): AccessCuInputObject[] {
  const result: AccessCuInputObject[] = [];
  getAccessesOrder().forEach((component) => {
    const access = accessesValues[component];
    const prevAccess = prevAccessesValues[component];
    const itemOptions = mergeAndUnlinkMultiSelectValues(
      prevAccess?.items?.map((item) => ({
        ...item,
        id: item.value as string,
      })),
      access?.items?.map((item) => ({ ...item, id: item.value as string })),
    );
    const hasScopeLevel = componentHasScopeLevel(component);
    const items = itemOptions
      ? itemOptions.map(({ id, unlink }) => ({ id, unlink }))
      : undefined;
    let scopeLevel: AccessScopeLevelEnum | undefined;
    if (hasScopeLevel) {
      if (itemOptions === null) {
        scopeLevel = access?.scopeLevel || AccessScopeLevelEnum.All;
      } else if (items?.length) {
        scopeLevel = AccessScopeLevelEnum.Selected;
      } else {
        scopeLevel = AccessScopeLevelEnum.All;
      }
    }
    if (access) {
      result.push({
        component,
        items,
        level: hasScopeLevel ? undefined : access.level?.value,
        scopeLevel,
      });
    } else if (prevAccess && !access && useDestroy) {
      result.push({
        component,
        _destroy: true,
      });
    }
  });
  return result;
}

export function getEmptyAccessValue(component: AccessComponentEnum) {
  return {
    component,
    items: [] as SelectOption[],
    itemsList: [] as SelectOption[],
    level: getAccessLevelOption(AccessLevelEnum.LimitedRead),
    scopeLevel: componentHasScopeLevel(component)
      ? AccessScopeLevelEnum.All
      : undefined,
    policy: AccessPolicyEnum.Team,
    isCustom: undefined,
  };
}

export function getEmptyAccessesValues() {
  const value = {};
  getAccessesOrder().forEach((component) => {
    value[component] = getEmptyAccessValue(component);
  });
  return value;
}

export function getAccessesValues(
  accesses: Access[] | null,
  policy: AccessPolicyEnum = AccessPolicyEnum.Team,
) {
  const result = {};
  if (accesses === null) return result;
  accesses.forEach((access) => {
    result[access.component] = {
      ...access,
      items: (access.itemIds || []).map((item) => ({
        value: item,
      })),
      itemsList: [],
      level: access.level ? getAccessLevelOption(access.level) : undefined,
      policy,
      isCustom: policy === AccessPolicyEnum.User,
    };
  });
  return result;
}

export function calculateActiveUsersPercentage(
  activeUsersCount: number,
  usersLimit?: number | null,
): number {
  if (_isNil(usersLimit)) {
    return 0;
  }
  if (usersLimit === 0) {
    return 100;
  }
  return Math.round((activeUsersCount * 100) / usersLimit);
}

export function getPlanState(activeUsersPercentage: number) {
  let type = PlanState.None;
  if (activeUsersPercentage >= 75) type = PlanState.Warning;
  if (activeUsersPercentage >= 100) type = PlanState.Attention;
  return type;
}
